import { getDatabase, ref, get, set, push, update, query, orderByChild, equalTo, DataSnapshot } from 'firebase/database';
import { recordActivity } from './activityApi';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage, formatDateOnlyForStorage } from '../Helpers/DateUtils';
import { Issue, IssueWithBuilding, IssuePriority, IssueComponentLink, IssueComponentCategory, ReportedIssue, ReportedIssueState } from '../types/issues';
import { WorkOrderPriority } from '../types/workOrders';
import { BaseType } from '../types/Enums';
import { translate } from '../types/EnumTranslations';
import { getBuildComponentBaseType, resolveBuildComponentTypeTranslation } from '../Helpers/BuildComponentTypesStore';

/** What the person converting chose for the work order (issueApi `convert`). */
export interface ConvertIssueInput {
    title: string;
    description?: string;
    priority?: WorkOrderPriority;
    /** YYYY-MM-DD (a stored `YYYY-MM-DD hh:mm:ss` is cut to the day) */
    dueDate?: string;
    /** Internal assignee */
    assignedTo?: string;
    /** E-mail the internal assignee (consumed by the assignment trigger). */
    sendAssignmentEmail?: boolean;
    requiresPhoto?: boolean;
    requiresComment?: boolean;
    requiresConditionUpdate?: boolean;
    /** The work order may link several components; the issue has at most one. */
    componentKeys?: string[];
    unitKey?: string;
    unitLabel?: string;
    enabledSections?: {
        notes?: boolean;
        checklists?: boolean;
        beforePictures?: boolean;
        progressPictures?: boolean;
    };
}

export class IssuesApi {
    private db = getDatabase();

    private getCurrentUser() {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }
        return user;
    }

    /**
     * Verify that the current user is an admin
     */
    private async verifyAdmin(): Promise<void> {
        const user = this.getCurrentUser();
        const adminRef = ref(this.db, `users/userList/${user.uid}/admin`);
        const adminSnapshot = await get(adminRef);
        if (!adminSnapshot.exists() || adminSnapshot.val() !== true) {
            throw new Error('Du måste vara administratör för att utföra denna åtgärd');
        }
    }

    /**
     * Validate and parse issue data from Firebase
     * Returns null if required fields are missing
     */
    private parseIssueData(
        data: unknown,
        key: string,
        buildingName?: string
    ): IssueWithBuilding | null {
        if (!data || typeof data !== 'object') {
            return null;
        }

        const d = data as Record<string, unknown>;

        // Required fields
        if (
            typeof d.description !== 'string' ||
            typeof d.buildingKey !== 'string'
        ) {
            console.warn(`Invalid issue data at ${key}: missing required fields`);
            return null;
        }

        // Validate priority if present
        if (d.priority && !Object.values(IssuePriority).includes(d.priority as IssuePriority)) {
            console.warn(`Invalid issue priority at ${key}: ${d.priority}`);
            return null;
        }

        return {
            key,
            buildingName: buildingName ?? 'Okänd byggnad',
            ...d
        } as IssueWithBuilding;
    }

    // ============ READ OPERATIONS ============

    /**
     * Get all active issues across all buildings
     */
    async getAll(): Promise<IssueWithBuilding[]> {
        // Fetch issues and building summaries in parallel
        const [issuesSnapshot, buildingsSnapshot] = await Promise.all([
            get(ref(this.db, 'issues/active')),
            get(ref(this.db, 'buildingsSummary'))
        ]);

        if (!issuesSnapshot.exists()) {
            return [];
        }

        // Map of buildingKey -> buildingName for the buildings that exist.
        // removeBuilding archives only the building, so issues of a removed
        // building stay in issues/active; they are treated as non-existent here.
        const buildingNames = new Map<string, string | undefined>();
        if (buildingsSnapshot.exists()) {
            buildingsSnapshot.forEach((child) => {
                buildingNames.set(child.key!, child.val()?.name);
            });
        }

        const issues: IssueWithBuilding[] = [];
        issuesSnapshot.forEach((child) => {
            const key = child.key;
            if (!key) return;

            const data = child.val();
            const buildingKey = data?.buildingKey;
            if (typeof buildingKey !== 'string' || !buildingNames.has(buildingKey)) return;
            const issue = this.parseIssueData(data, key, buildingNames.get(buildingKey));

            if (issue) {
                issues.push(issue);
            }
        });

        // Sort by createdAt descending (newest first)
        return issues.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }

    /**
     * Get issues for a specific building
     */
    async getByBuilding(buildingKey: string): Promise<Issue[]> {
        const snapshot = await get(ref(this.db, 'issues/active'));

        if (!snapshot.exists()) {
            return [];
        }

        const issues: Issue[] = [];
        snapshot.forEach((child) => {
            const key = child.key;
            if (!key) return;

            const data = child.val();
            if (data?.buildingKey === buildingKey) {
                const issue = this.parseIssueData(data, key);
                if (issue) {
                    issues.push(issue);
                }
            }
        });

        // Sort by createdAt descending (newest first)
        return issues.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }

    /**
     * Get all archived issues
     */
    async getArchive(): Promise<IssueWithBuilding[]> {
        const [archiveSnapshot, buildingsSnapshot] = await Promise.all([
            get(ref(this.db, 'issues/archive')),
            get(ref(this.db, 'buildingsSummary'))
        ]);

        if (!archiveSnapshot.exists()) {
            return [];
        }

        // Build map of buildingKey -> buildingName
        const buildingNames: Record<string, string> = {};
        if (buildingsSnapshot.exists()) {
            buildingsSnapshot.forEach((child) => {
                const summary = child.val();
                if (summary?.name) {
                    buildingNames[child.key!] = summary.name;
                }
            });
        }

        const issues: IssueWithBuilding[] = [];
        archiveSnapshot.forEach((child) => {
            const key = child.key;
            if (!key) return;

            const data = child.val();
            const buildingName = data?.buildingKey ? buildingNames[data.buildingKey] : undefined;
            const issue = this.parseIssueData(data, key, buildingName);

            if (issue) {
                issues.push(issue);
            }
        });

        // Sort by archivedAt descending (newest first)
        return issues.sort((a, b) => (b.archivedAt || '').localeCompare(a.archivedAt || ''));
    }

    /**
     * Get archived issues for a specific year
     * Client-side filter by extracting year from archivedAt string
     */
    async getArchiveByYear(year: number): Promise<IssueWithBuilding[]> {
        const allArchived = await this.getArchive();

        // Filter by year extracted from archivedAt (format: "YYYY-MM-DD hh:mm:ss")
        return allArchived.filter(issue => {
            if (!issue.archivedAt) return false;
            const archivedYear = parseInt(issue.archivedAt.substring(0, 4), 10);
            return archivedYear === year;
        });
    }

    /**
     * Get archived issues for a specific building
     * Note: Archive is flat (issues/archive/{key}), not nested by year
     */
    async getArchivedByBuilding(buildingKey: string): Promise<Issue[]> {
        const snapshot = await get(ref(this.db, 'issues/archive'));

        if (!snapshot.exists()) {
            return [];
        }

        const issues: Issue[] = [];
        snapshot.forEach((child) => {
            const key = child.key;
            if (!key) return;

            const data = child.val();
            if (data?.buildingKey === buildingKey) {
                const issue = this.parseIssueData(data, key);
                if (issue) {
                    issues.push(issue);
                }
            }
        });

        return issues;
    }

    /**
     * Get a single issue by key
     */
    async getById(issueKey: string): Promise<IssueWithBuilding | null> {
        const [issueSnapshot, buildingsSnapshot] = await Promise.all([
            get(ref(this.db, `issues/active/${issueKey}`)),
            get(ref(this.db, 'buildingsSummary'))
        ]);

        if (!issueSnapshot.exists()) {
            return null;
        }

        const data = issueSnapshot.val();

        // Get building name
        let buildingName: string | undefined;
        if (data?.buildingKey && buildingsSnapshot.exists()) {
            const buildingSummary = buildingsSnapshot.child(data.buildingKey).val();
            buildingName = buildingSummary?.name;
        }

        return this.parseIssueData(data, issueKey, buildingName);
    }

    /**
     * Get a single archived issue by key
     */
    async getArchivedById(issueKey: string): Promise<Issue | null> {
        const snapshot = await get(ref(this.db, `issues/archive/${issueKey}`));

        if (!snapshot.exists()) {
            return null;
        }

        return this.parseIssueData(snapshot.val(), issueKey);
    }

    /**
     * Issues reported from a work order (Felanmäl), active and archived,
     * newest first. The order stores nothing; this is a query on
     * `sourceWorkOrderKey` (indexed on both roots).
     */
    async getBySourceWorkOrder(workOrderKey: string): Promise<ReportedIssue[]> {
        const [activeSnapshot, archiveSnapshot] = await Promise.all(
            ['active', 'archive'].map((root) => get(query(
                ref(this.db, `issues/${root}`),
                orderByChild('sourceWorkOrderKey'),
                equalTo(workOrderKey)
            )))
        );
        const issues: ReportedIssue[] = [];
        const collect = (snapshot: DataSnapshot, archived: boolean) => {
            snapshot.forEach((child) => {
                const issue = child.key ? this.parseIssueData(child.val(), child.key) : null;
                if (!issue) return;
                const state: ReportedIssueState = !archived
                    ? 'active'
                    : issue.convertedToWorkOrderKey ? 'converted' : 'archived';
                issues.push({ issue, state });
            });
        };
        collect(activeSnapshot, false);
        collect(archiveSnapshot, true);
        return issues.sort((a, b) => b.issue.createdAt.localeCompare(a.issue.createdAt));
    }

    // ============ MUTATIONS ============

    /**
     * Create a new issue
     */
    async create(data: Omit<Issue, 'key' | 'createdBy' | 'createdAt'>): Promise<string> {
        const user = this.getCurrentUser();

        if (!data.description?.trim()) {
            throw new Error('Beskrivning krävs');
        }

        if (!data.buildingKey) {
            throw new Error('Byggnad krävs');
        }

        const issuesRef = ref(this.db, 'issues/active');
        const newRef = push(issuesRef);
        const key = newRef.key!;

        const issue: Issue = {
            key,
            buildingKey: data.buildingKey,
            componentKey: data.componentKey || undefined,
            description: data.description.trim(),
            priority: data.priority || IssuePriority.MEDIUM,
            photos: data.photos || undefined,
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date()),
            source: data.source || undefined,
            sourceWorkOrderKey: data.sourceWorkOrderKey || undefined
        };

        // Remove undefined values for Firebase
        const cleanIssue = Object.fromEntries(
            Object.entries(issue).filter(([_, v]) => v !== undefined)
        );

        // issue.created is logged by ReportIssueModal, the one create flow.
        await set(newRef, cleanIssue);
        return key;
    }

    /**
     * Convert an issue to a work order through the `issueApi` callable (ios#1342):
     * the server creates the order and archives the issue with
     * `convertedToWorkOrderKey` in one update, and refuses an issue that is no
     * longer active ("Felanmälan är redan hanterad"). Admin or superadmin.
     * Delegation to an external user is a separate guestTokenApi call.
     */
    async convertToWorkOrder(issueKey: string, workOrderData: ConvertIssueInput): Promise<string> {
        if (!workOrderData.title?.trim()) {
            throw new Error('Titel krävs');
        }
        const result = await this.callIssueApi<{ workOrderKey: string; buildingKey: string }>({
            action: 'convert',
            issueKey,
            ...workOrderData,
            title: workOrderData.title.trim(),
            // The callable takes the day; it stores the end of it.
            dueDate: workOrderData.dueDate ? workOrderData.dueDate.split(' ')[0] : undefined,
        });
        recordActivity('issue.converted', { buildingKey: result.buildingKey, targetKey: issueKey, label: workOrderData.title.trim() });
        return result.workOrderKey;
    }

    /**
     * Archive an issue without creating a work order, through the `issueApi`
     * callable, with an optional reason. Admin or superadmin.
     */
    async archiveDirectly(issueKey: string, reason?: string): Promise<void> {
        const result = await this.callIssueApi<{ buildingKey: string }>({
            action: 'archive',
            issueKey,
            reason: reason?.trim() || undefined,
        });
        recordActivity('issue.archived', { buildingKey: result.buildingKey, targetKey: issueKey });
    }

    private async callIssueApi<T>(request: Record<string, unknown>): Promise<T> {
        // Loaded on use: the functions SDK does not load under jsdom, and many tests import this module.
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const callable = httpsCallable<Record<string, unknown>, T>(getFunctions(undefined, 'europe-north1'), 'issueApi');
        // Firebase rejects undefined fields in callable data.
        const data = Object.fromEntries(Object.entries(request).filter(([, v]) => v !== undefined));
        const result = await callable(data);
        return result.data;
    }

    /**
     * Resolve a category input against the type config: the specific type must
     * exist and belong to the given base type. The stored label is the specific
     * type's Swedish name, else the base type's.
     */
    private resolveComponentCategory(
        input: { baseType: BaseType; specificType?: string }
    ): IssueComponentCategory {
        if (!Object.values(BaseType).includes(input.baseType)) {
            throw new Error('Ogiltig komponenttyp');
        }
        const specificType = input.specificType?.trim() || undefined;
        if (specificType) {
            const definedBaseType = getBuildComponentBaseType(specificType);
            if (!definedBaseType) {
                throw new Error('Okänd komponenttyp');
            }
            if (definedBaseType !== input.baseType) {
                throw new Error('Komponenttypen tillhör en annan bastyp');
            }
        }
        const label = resolveBuildComponentTypeTranslation(specificType) || translate(input.baseType);
        return specificType
            ? { baseType: input.baseType, specificType, label }
            : { baseType: input.baseType, label };
    }

    /**
     * Set what an issue is about (admin only): a specific component, a
     * component category when no component exists, or nothing. The two are
     * mutually exclusive, so the one not chosen is cleared in the same update.
     */
    async updateComponentLink(issueKey: string, link: IssueComponentLink): Promise<void> {
        await this.verifyAdmin();

        const issueRef = ref(this.db, `issues/active/${issueKey}`);
        const snapshot = await get(issueRef);

        if (!snapshot.exists()) {
            throw new Error('Felanmälan hittades inte');
        }

        const componentKey = link && 'componentKey' in link ? link.componentKey.trim() : '';
        const componentCategory = link && 'componentCategory' in link
            ? this.resolveComponentCategory(link.componentCategory)
            : null;

        await update(issueRef, {
            componentKey: componentKey || null,
            componentCategory
        });
        recordActivity('issue.updated', { buildingKey: snapshot.val()?.buildingKey, targetKey: issueKey });
    }

    /**
     * Update an issue's priority (admin only).
     */
    async updatePriority(issueKey: string, priority: IssuePriority): Promise<void> {
        await this.verifyAdmin();

        const issueRef = ref(this.db, `issues/active/${issueKey}`);
        const snapshot = await get(issueRef);

        if (!snapshot.exists()) {
            throw new Error('Felanmälan hittades inte');
        }

        await update(issueRef, { priority });
        recordActivity('issue.updated', { buildingKey: snapshot.val()?.buildingKey, targetKey: issueKey });
    }

    // ============ SNOOZE OPERATIONS ============

    /**
     * Snooze an issue until a specific date (admin only)
     * - Sets snoozedUntil, snoozedBy, snoozedAt fields
     * - Issue will be hidden from active list until snoozedUntil date arrives
     */
    async snoozeIssue(issueKey: string, snoozeDate: string): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        // Validate snooze date is in the future
        const today = formatDateOnlyForStorage(new Date());
        if (snoozeDate <= today) {
            throw new Error('Snooze-datum måste vara i framtiden');
        }

        const issueRef = ref(this.db, `issues/active/${issueKey}`);
        const snapshot = await get(issueRef);

        if (!snapshot.exists()) {
            throw new Error('Felanmälan hittades inte');
        }

        await update(issueRef, {
            snoozedUntil: snoozeDate,
            snoozedBy: user.uid,
            snoozedAt: formatDateForStorage(new Date())
        });
        recordActivity('issue.updated', { buildingKey: snapshot.val()?.buildingKey, targetKey: issueKey });
    }

    /**
     * Unsnooze an issue, making it visible in the active list again (admin only)
     * - Clears snoozedUntil, snoozedBy, snoozedAt fields
     */
    async unsnoozeIssue(issueKey: string): Promise<void> {
        await this.verifyAdmin();

        const issueRef = ref(this.db, `issues/active/${issueKey}`);
        const snapshot = await get(issueRef);

        if (!snapshot.exists()) {
            throw new Error('Felanmälan hittades inte');
        }

        await update(issueRef, {
            snoozedUntil: null,
            snoozedBy: null,
            snoozedAt: null
        });
        recordActivity('issue.updated', { buildingKey: snapshot.val()?.buildingKey, targetKey: issueKey });
    }
}

// Export singleton instance
export const issuesApi = new IssuesApi();
